/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { getCheckoutState } from '@msdyn365-commerce/global-state';

import { ICheckoutCustomerAccountPaymentViewProps } from '@msdyn365-commerce-modules/checkout/src/modules/checkout-customer-account-payment/checkout-customer-account-payment';
import { IAccountPaymentEditViewForm } from '@msdyn365-commerce-modules/checkout/src/modules/checkout-customer-account-payment/components/get-account-payment-form-edit-mode';
import { IAccountPaymentSummaryViewForm } from '@msdyn365-commerce-modules/checkout/src/modules/checkout-customer-account-payment/components/get-account-payment-form-summary-mode';
import { ICheckoutCustomerAccountPaymentProps } from '../definition-extensions/checkout-customer-account-payment.ext.props.autogenerated';
import { ICheckoutCustomerAccountPaymentData } from '../../../dataTypes/checkout-customer-account-payment.data';
import { PaymentStateShareInput } from '../../../actions/payment-state-share.action';
import { CustomerBalances } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

export interface ICreditLimitInfo {
    totalAmount: number;
    creditLimit: number;
    showError: boolean;
}

const changePaymentMethodToCC = async (
    props: ICheckoutCustomerAccountPaymentViewProps & ICheckoutCustomerAccountPaymentProps<ICheckoutCustomerAccountPaymentData>
) => {
    const checkoutState = await getCheckoutState(props.context.actionContext);
    const actionContext = props.context.actionContext;
    if (checkoutState) {
        await checkoutState.updateCustomerAccountAmount({ newAmount: 0 });
        actionContext.update(new PaymentStateShareInput(), { isCreditCardSelected: true, isAccountPaymentSelected: false });
        props.moduleState.setHasError(false);
    }
};

const changePaymentMethodToOnAccount = (
    props: ICheckoutCustomerAccountPaymentViewProps & ICheckoutCustomerAccountPaymentProps<ICheckoutCustomerAccountPaymentData>,
    setCheckCreditInfo: React.Dispatch<React.SetStateAction<ICreditLimitInfo | null>>
) => {
    const actionContext = props.context.actionContext;
    const creditInfo = checkCreditLimit(props);
    setCheckCreditInfo(creditInfo);
    applyCreditAmount(props, creditInfo);
    actionContext.update(new PaymentStateShareInput(), { isCreditCardSelected: false, isAccountPaymentSelected: true });
};

export const SummaryForm: React.FC<IAccountPaymentSummaryViewForm> = props => {
    const { formProps, label, addPaymentButton, appliedLine, bottomBorder, paymentAmountInput } = props;
    return (
        <Node {...formProps}>
            <>
                {label}
                {paymentAmountInput}
                {addPaymentButton}
                {appliedLine}
                {bottomBorder}
            </>
        </Node>
    );
};

export const EditForm: React.FC<IAccountPaymentEditViewForm> = props => {
    const { formProps, inputLabel, alert, appliedLine, bottomBorder } = props;
    return (
        <Node {...formProps}>
            <>
                <div className='ms-checkout-customer-account__account-credit'>Account Credit: </div>
                {inputLabel}
                {alert}
                {appliedLine}
                {bottomBorder}
            </>
        </Node>
    );
};

const getAvailableCreditLimit = (creditBalances: CustomerBalances | undefined) => {
    let availableCredit = 0;
    if (!creditBalances) {
        return 0;
    }

    if (creditBalances.InvoiceAccountCreditLimit === 0) {
        availableCredit = creditBalances.CreditLimit - creditBalances.Balance - creditBalances.PendingBalance;
    } else {
        availableCredit =
            creditBalances.InvoiceAccountCreditLimit - creditBalances.InvoiceAccountBalance - creditBalances.InvoiceAccountPendingBalance;
    }
    return availableCredit;
};

const checkCreditLimit = (
    props: ICheckoutCustomerAccountPaymentViewProps & ICheckoutCustomerAccountPaymentProps<ICheckoutCustomerAccountPaymentData>
) => {
    const {
        data: { customerInformation }
    } = props;
    const totalAmount = props.data.checkout.result?.checkoutCart.cart.AmountDue;
    const creditLimit = getAvailableCreditLimit(props.data.creditBalances?.result);
    const showError = customerInformation.result?.MandatoryCreditLimit
        ? creditLimit && totalAmount && creditLimit - totalAmount > 0
            ? false
            : true
        : false;
    const creditLimitInfo: ICreditLimitInfo = {
        creditLimit: creditLimit ? Number(creditLimit) : 0,
        showError: showError,
        totalAmount: totalAmount ? Number(totalAmount) : 0
    };
    return creditLimitInfo;
};

const applyCreditAmount = async (
    props: ICheckoutCustomerAccountPaymentViewProps & ICheckoutCustomerAccountPaymentProps<ICheckoutCustomerAccountPaymentData>,
    checkCreditInfo: ICreditLimitInfo
) => {
    const checkoutState = props.data.checkout.result;
    if (checkoutState && checkCreditInfo.totalAmount && checkCreditInfo.totalAmount > 0) {
        if (checkCreditInfo.showError) {
            await checkoutState.updateCustomerAccountAmount({ newAmount: 0 });
            props.moduleState.setHasError(true);
        } else {
            await checkoutState.updateCustomerAccountAmount({ newAmount: checkCreditInfo.totalAmount });
            props.moduleState.setHasError(false);
            props.moduleState.onReady();
        }
    }
};

const loadCCDefault = (
    props: ICheckoutCustomerAccountPaymentViewProps & ICheckoutCustomerAccountPaymentProps<ICheckoutCustomerAccountPaymentData>
) => {
    const actionContext = props.context.actionContext;
    actionContext.update(new PaymentStateShareInput(), { isCreditCardSelected: true, isAccountPaymentSelected: false });
};

const CheckoutCustomerAccountView: React.FC<ICheckoutCustomerAccountPaymentViewProps &
    ICheckoutCustomerAccountPaymentProps<ICheckoutCustomerAccountPaymentData>> = props => {
    const {
        checkoutCustomerAccount,
        checkoutErrorRef,
        data: { getPaymentState, customerInformation },
        context: {
            app: {
                config: { labelChangePaymentMethodToOnAccount, labelChangePaymentMethodToCard, errorMessageForInsufficientCredit }
            }
        },
        editView,
        resources: { totalCreditApplied, accountCreditHeading }
    } = props;

    const [checkCreditInfo, setCheckCreditInfo] = React.useState<ICreditLimitInfo | null>(null);
    const billingAddress = props.moduleState.getModuleByTypeName('bc-checkout-billing-address');
    const shippingAddress = props.moduleState.getModuleByTypeName('bc-checkout-shipping-address');
    const deliveryOptions = props.moduleState.getModuleByTypeName('checkout-delivery-options');

    React.useEffect(() => {
        if (deliveryOptions.isReady) {
            const creditInfo = checkCreditLimit(props);
            setCheckCreditInfo(creditInfo);
            applyCreditAmount(props, creditInfo);
            props.moduleState.onReady();
        }
    }, [shippingAddress.isReady, billingAddress.isReady, deliveryOptions.isReady]);

    React.useEffect(() => {
        if (customerInformation.result?.AllowOnAccountPayment === false) {
            loadCCDefault(props);
        }
    }, [customerInformation.result?.AllowOnAccountPayment]);
    if (customerInformation.result?.AllowOnAccountPayment) {
        if (getPaymentState.result?.isAccountPaymentSelected) {
            return (
                <Module {...checkoutCustomerAccount} ref={checkoutErrorRef}>
                    <>
                        {editView && (
                            <>
                                {editView.customerName}
                                {editView.customerAccountNumber}
                                {editView.customerSince}
                            </>
                        )}
                    </>
                    {checkCreditInfo && !checkCreditInfo.showError && (
                        <>
                            <div className='ms-checkout-customer-account__account-credit'>{accountCreditHeading}</div>
                            <div className='ms-checkout-customer-account__info-container'>
                                <div className='ms-checkout-customer-account__applied-line'>
                                    <span className='ms-checkout-customer-account__applied-label'>{totalCreditApplied}</span>
                                    <span className='ms-checkout-customer-account__applied-amount'>
                                        {props.context?.cultureFormatter?.formatCurrency(checkCreditInfo.totalAmount)}
                                    </span>
                                </div>
                                <a
                                    href='javascript:void(0)'
                                    onClick={() => changePaymentMethodToCC(props)}
                                    className='ms-checkout-customer-account__toggle'
                                >
                                    {labelChangePaymentMethodToCard}
                                </a>
                            </div>
                        </>
                    )}
                    {checkCreditInfo && checkCreditInfo.showError && (
                        <>
                            <div className='ms-checkout-customer-account__error-message'>{errorMessageForInsufficientCredit}</div>
                            <a
                                href='javascript:void(0)'
                                onClick={() => changePaymentMethodToCC(props)}
                                className='ms-checkout-customer-account__toggle'
                            >
                                {labelChangePaymentMethodToCard}
                            </a>
                        </>
                    )}
                </Module>
            );
        }
    }

    if (customerInformation.result?.AllowOnAccountPayment) {
        return (
            <a
                href='javascript:void(0)'
                onClick={() => changePaymentMethodToOnAccount(props, setCheckCreditInfo)}
                className='ms-checkout-customer-account__toggle'
            >
                {labelChangePaymentMethodToOnAccount}
            </a>
        );
    }
    return null;
};

export default CheckoutCustomerAccountView;
